(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const setPlaybackIcon = (button, paused) => {
    if (!button) return;
    button.setAttribute('aria-pressed', String(paused));
    button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
    button.querySelector('[data-icon-play]')?.toggleAttribute('hidden', !paused);
    button.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', paused);
  };

  class AurumHero extends HTMLElement {
    connectedCallback() {
      this.endAt = Date.parse(this.dataset.endAt || '');
      this.afterEnd = this.dataset.afterEnd || 'hide';
      this.countdown = this.querySelector('[data-countdown]');
      this.playback = this.querySelector('[data-hero-playback]');
      this.videos = Array.from(this.querySelectorAll('video'));

      this.videos.forEach((video) => {
        video.muted = true;
        video.playsInline = true;
        video.addEventListener('ended', this.handleVideoEnded);
      });

      this.playback?.addEventListener('click', this.handlePlayback);
      this.mediaQuery = window.matchMedia('(max-width: 767px)');
      this.mediaQuery.addEventListener?.('change', this.syncActiveVideo);

      this.updateCountdown();
      this.countdownTimer = window.setInterval(() => this.updateCountdown(), 1000);
      this.syncActiveVideo();
    }

    disconnectedCallback() {
      window.clearInterval(this.countdownTimer);
      this.playback?.removeEventListener('click', this.handlePlayback);
      this.mediaQuery?.removeEventListener?.('change', this.syncActiveVideo);
      this.videos?.forEach((video) => video.removeEventListener('ended', this.handleVideoEnded));
    }

    handleVideoEnded = (event) => {
      if (event.currentTarget !== this.activeVideo || this.userPaused || document.hidden) return;
      event.currentTarget.currentTime = 0;
      event.currentTarget.play().catch(() => {});
    };

    handlePlayback = () => {
      if (!this.activeVideo) return;
      this.userPaused = !this.activeVideo.paused;
      if (this.userPaused) {
        this.activeVideo.pause();
      } else {
        this.activeVideo.play().catch(() => {});
      }
      setPlaybackIcon(this.playback, this.userPaused);
    };

    syncActiveVideo = () => {
      const selector = this.mediaQuery?.matches
        ? '.aurum-hero__video-wrap--mobile video'
        : '.aurum-hero__video-wrap--desktop video';
      const nextVideo = this.querySelector(selector) || this.videos[0];

      this.videos.forEach((video) => {
        if (video !== nextVideo) video.pause();
      });

      this.activeVideo = nextVideo;
      if (this.activeVideo && !this.userPaused && !reducedMotion.matches) {
        this.activeVideo.play().catch(() => {});
      }
      setPlaybackIcon(this.playback, Boolean(this.userPaused || reducedMotion.matches));
    };

    updateCountdown() {
      if (!this.countdown || !Number.isFinite(this.endAt)) return;
      const remaining = Math.max(0, this.endAt - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      };

      Object.entries(values).forEach(([key, value]) => {
        const target = this.countdown.querySelector(`[data-${key}]`);
        if (target) target.textContent = String(value).padStart(2, '0');
      });

      if (remaining > 0) return;
      window.clearInterval(this.countdownTimer);
      if (this.afterEnd === 'hide') this.countdown.hidden = true;
      if (this.afterEnd === 'message') {
        const message = this.dataset.endMessage || '';
        if (message) this.countdown.innerHTML = `<span class="aurum-countdown__message">${message}</span>`;
      }
    }
  }

  class AurumScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-aurum-track]');
      if (!this.track || this.dataset.aurumReady === 'true') return;
      this.dataset.aurumReady = 'true';
      this.slides = Array.from(this.track.querySelectorAll('[data-aurum-slide]'));
      this.prevButtons = Array.from(this.querySelectorAll('[data-aurum-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-aurum-next]'));
      this.dots = Array.from(this.querySelectorAll('[data-aurum-dot]'));
      this.playback = this.querySelector('[data-carousel-playback]');
      this.isVideoCarousel = this.hasAttribute('data-video-carousel');
      this.currentIndex = 0;
      this.userPaused = false;
      this.isVisible = true;

      this.prevButtons.forEach((button) => button.addEventListener('click', () => this.goTo(this.currentIndex - 1)));
      this.nextButtons.forEach((button) => button.addEventListener('click', () => this.goTo(this.currentIndex + 1)));
      this.dots.forEach((button, index) => button.addEventListener('click', () => this.goTo(index)));
      this.playback?.addEventListener('click', this.togglePlayback);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      if (this.isVideoCarousel) {
        this.querySelectorAll('video').forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          video.addEventListener('ended', this.loopActiveVideo);
        });
        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            this.isVisible = entry.isIntersecting;
            this.syncVideo();
          },
          { threshold: 0.35 }
        );
        this.visibilityObserver.observe(this);
      }

      this.updateUI();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.track?.removeEventListener('scroll', this.handleScroll);
      this.playback?.removeEventListener('click', this.togglePlayback);
    }

    loopActiveVideo = (event) => {
      const active = this.slides[this.currentIndex]?.querySelector('video');
      if (event.currentTarget !== active || this.userPaused || !this.isVisible || document.hidden) return;
      active.currentTime = 0;
      active.play().catch(() => {});
    };

    togglePlayback = () => {
      const active = this.slides[this.currentIndex]?.querySelector('video');
      if (!active) return;
      this.userPaused = !active.paused;
      this.syncVideo();
    };

    handleScroll = () => {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        const trackRect = this.track.getBoundingClientRect();
        const scrollPadding = Number.parseFloat(window.getComputedStyle(this.track).scrollPaddingLeft) || 0;
        const trackStart = trackRect.left + scrollPadding;
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left - trackStart);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== this.currentIndex) {
          this.currentIndex = closestIndex;
          this.updateUI();
        }
      });
    };

    goTo(index) {
      const nextIndex = clamp(index, 0, this.slides.length - 1);
      this.slides[nextIndex]?.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start',
      });
      this.currentIndex = nextIndex;
      this.updateUI();
    }

    updateUI() {
      const atStart = this.currentIndex === 0;
      const atEnd = this.currentIndex === this.slides.length - 1;
      this.prevButtons.forEach((button) => (button.disabled = atStart));
      this.nextButtons.forEach((button) => (button.disabled = atEnd));
      this.dots.forEach((button, index) => button.setAttribute('aria-current', String(index === this.currentIndex)));
      this.slides.forEach((slide, index) => slide.toggleAttribute('data-active', index === this.currentIndex));
      this.syncVideo();
    }

    syncVideo() {
      if (!this.isVideoCarousel) return;
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        const shouldPlay = index === this.currentIndex && this.isVisible && !this.userPaused && !reducedMotion.matches;
        if (shouldPlay) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
      setPlaybackIcon(this.playback, Boolean(this.userPaused || reducedMotion.matches));
    }
  }

  class AurumStory extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-aurum-reveal]'));
      if (!this.items.length) return;

      this.revealObserver = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.target.classList.toggle('is-revealed', entry.isIntersecting)),
        { threshold: 0.2, rootMargin: '0px 0px -12% 0px' }
      );
      this.items.forEach((item) => this.revealObserver.observe(item));
      window.addEventListener('scroll', this.updateProgress, { passive: true });
      window.addEventListener('resize', this.updateProgress, { passive: true });
      this.updateProgress();
    }

    disconnectedCallback() {
      this.revealObserver?.disconnect();
      window.removeEventListener('scroll', this.updateProgress);
      window.removeEventListener('resize', this.updateProgress);
    }

    updateProgress = () => {
      if (reducedMotion.matches || window.innerWidth < 768) return;
      window.cancelAnimationFrame(this.progressFrame);
      this.progressFrame = window.requestAnimationFrame(() => {
        this.items.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const progress = clamp((window.innerHeight - rect.top) / (window.innerHeight + rect.height), 0, 1);
          item.style.setProperty('--aurum-progress', progress.toFixed(3));
        });
      });
    };
  }

  class AurumLenses extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-lens-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-lens-panel]'));
      this.dots = Array.from(this.querySelectorAll('[data-lens-dot]'));
      this.mobileContent = this.querySelector('[data-lens-mobile-content]');
      this.mobileTitle = this.querySelector('[data-lens-mobile-title]');
      this.mobileCopy = this.querySelector('[data-lens-mobile-copy]');
      this.currentIndex = 0;
      this.mobileTransitionToken = 0;
      this.mobileAnimations = [];

      this.tabs.forEach((tab, index) =>
        tab.addEventListener('click', () => this.select(index, false, index === this.currentIndex ? 0 : index > this.currentIndex ? 1 : -1))
      );
      this.dots.forEach((dot, index) =>
        dot.addEventListener('click', () => this.select(index, false, index === this.currentIndex ? 0 : index > this.currentIndex ? 1 : -1))
      );
      this.querySelector('[data-lens-prev]')?.addEventListener('click', () => this.select(this.currentIndex - 1, true, -1));
      this.querySelector('[data-lens-next]')?.addEventListener('click', () => this.select(this.currentIndex + 1, true, 1));

      this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      this.select(0);
    }

    disconnectedCallback() {
      this.mobileTransitionToken += 1;
      this.clearMobileTransition();
    }

    handleTouchStart = (event) => {
      this.touchStartX = event.changedTouches[0]?.clientX;
    };

    handleTouchEnd = (event) => {
      if (this.touchStartX == null) return;
      const delta = event.changedTouches[0]?.clientX - this.touchStartX;
      this.touchStartX = null;
      if (Math.abs(delta) < 48) return;
      const direction = delta < 0 ? 1 : -1;
      this.select(this.currentIndex + direction, true, direction);
    };

    select(index, wrap = false, direction = 0) {
      const count = this.tabs.length;
      if (!count) return;
      const nextIndex = wrap ? (index + count) % count : clamp(index, 0, count - 1);
      this.currentIndex = nextIndex;

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === nextIndex;
        tab.setAttribute('aria-selected', String(active));
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === nextIndex;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === nextIndex)));

      const activeTab = this.tabs[nextIndex];
      const label = activeTab?.querySelector('.aurum-lenses__tab-label');
      const description = activeTab?.querySelector('.aurum-lenses__description');
      this.updateMobileContent(label?.innerHTML || '', description?.innerHTML || '', direction);
    }

    updateMobileContent(label, description, direction) {
      const applyContent = () => {
        if (this.mobileTitle) this.mobileTitle.innerHTML = label;
        if (this.mobileCopy) this.mobileCopy.innerHTML = description;
      };

      const shouldAnimate =
        this.mobileContent && window.innerWidth < 768 && direction !== 0 && !reducedMotion.matches;
      const transitionToken = ++this.mobileTransitionToken;
      this.clearMobileTransition();

      if (!shouldAnimate) {
        applyContent();
        return;
      }

      const outgoingX = direction > 0 ? '-105%' : '105%';
      const incomingX = direction > 0 ? '105%' : '-105%';
      const outgoingContent = this.mobileContent.cloneNode(true);
      outgoingContent.removeAttribute('data-lens-mobile-content');
      outgoingContent.setAttribute('aria-hidden', 'true');
      outgoingContent.classList.add('aurum-lenses__mobile-content--outgoing');
      this.mobileContent.parentElement?.appendChild(outgoingContent);
      applyContent();

      const animationOptions = {
        duration: 340,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      };
      const outgoing = outgoingContent.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(${outgoingX})` }],
        animationOptions
      );
      const incoming = this.mobileContent.animate(
        [{ transform: `translateX(${incomingX})` }, { transform: 'translateX(0)' }],
        animationOptions
      );
      this.mobileAnimations = [outgoing, incoming];

      Promise.all([outgoing.finished.catch(() => {}), incoming.finished.catch(() => {})]).then(() => {
        if (transitionToken !== this.mobileTransitionToken) return;
        this.clearMobileTransition();
      });
    }

    clearMobileTransition() {
      this.mobileAnimations?.forEach((animation) => animation.cancel());
      this.mobileAnimations = [];
      this.querySelectorAll('.aurum-lenses__mobile-content--outgoing').forEach((content) => content.remove());
    }
  }

  const initStickyHandoff = () => {
    const handoff = document.querySelector('[data-aurum-product-handoff]');
    if (!handoff || !document.body.classList.contains('storm-aurum')) return;
    if (document.body.dataset.aurumStickyBound === 'true') return;
    document.body.dataset.aurumStickyBound = 'true';

    const update = () => {
      const absoluteTop = handoff.getBoundingClientRect().top + window.scrollY;
      const armAt = absoluteTop - window.innerHeight * 0.8;
      document.body.classList.toggle('aurum-sticky-ready', window.scrollY >= armAt);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  if (!customElements.get('aurum-hero')) customElements.define('aurum-hero', AurumHero);
  if (!customElements.get('aurum-scroller')) customElements.define('aurum-scroller', AurumScroller);
  if (!customElements.get('aurum-story')) customElements.define('aurum-story', AurumStory);
  if (!customElements.get('aurum-lenses')) customElements.define('aurum-lenses', AurumLenses);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyHandoff, { once: true });
  } else {
    initStickyHandoff();
  }
  document.addEventListener('shopify:section:load', initStickyHandoff);
})();

